import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Define database types for better TypeScript support
// In a production app, you'd generate these from your Supabase database
// using the Supabase CLI: supabase gen types typescript --local > types/supabase.ts
export interface Database {
  public: {
    Tables: {
      // Define your tables here
      users: any;
      messages: any;
      rooms: any;
      trusted_devices: any;
      read_receipts: any;
      auth_challenges: any;
      magic_link_sessions: any;
    };
    Views: {
      // Define your views here
    };
    Functions: {
      // Define your functions here
    };
    Enums: {
      // Define your enums here
    };
  };
}

// Configuration options for the Supabase client
export interface SupabaseOptions {
  auth?: {
    autoRefreshToken?: boolean;
    persistSession?: boolean;
    detectSessionInUrl?: boolean;
  };
  realtime?: {
    params?: {
      eventsPerSecond?: number;
    };
  };
  global?: {
    headers?: Record<string, string>;
  };
}

// Default options for the Supabase client
const defaultOptions: SupabaseOptions = {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
};

// Singleton instance of the Supabase client
let supabaseInstance: SupabaseClient<Database> | null = null;

/**
 * Creates or returns a singleton Supabase client instance
 * @param options Optional configuration options to override defaults
 * @returns The Supabase client instance
 */
export function getSupabaseClient(options: SupabaseOptions = {}): SupabaseClient<Database> {
  if (supabaseInstance) return supabaseInstance;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
     || '';
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
     || '';

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase URL and anon key are required. Check your environment variables.');
  }

  // Merge provided options with defaults
  const mergedOptions: any = {
    ...defaultOptions,
    ...options,
    db: {
      schema: 'api' // Point directly at api schema
    },
    auth: {
      ...defaultOptions.auth,
      ...options.auth,
    },
    realtime: {
      ...defaultOptions.realtime,
      ...options.realtime,
    },
  };

  supabaseInstance = createClient<Database>(supabaseUrl, supabaseAnonKey, mergedOptions);
  return supabaseInstance;
}

/**
 * Creates a new Supabase client instance (non-singleton) with the specified options
 * This is useful for cases where a different configuration is needed
 * @param options Optional configuration options to override defaults
 * @returns A new Supabase client instance
 */
export function createSupabaseClient(options: SupabaseOptions = {}): SupabaseClient<Database> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
     || '';
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
     || '';

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase URL and anon key are required. Check your environment variables.');
  }

  const mergedOptions: any = {
    ...defaultOptions,
    ...options,
    db: {
      schema: 'api' // Point directly at api schema
    },
    auth: {
      ...defaultOptions.auth,
      ...options.auth,
    },
    realtime: {
      ...defaultOptions.realtime,
      ...options.realtime,
    },
  };

  return createClient<Database>(supabaseUrl, supabaseAnonKey, mergedOptions);
}

/**
 * Creates a Supabase client for middleware usage
 * @param request The NextRequest object from middleware
 * @returns A Supabase client instance configured for middleware
 */
export function createMiddlewareSupabaseClient(request: any): SupabaseClient<Database> {
  return createSupabaseClient();
}

// Export a default client instance for easy imports
const supabase = getSupabaseClient();
export { supabase };
export default supabase;

